import { useState } from "react";
import axios from "axios";
import { m, AnimatePresence } from "framer-motion";
import { ArrowRight, Check, Loader2, PartyPopper, Home, Paintbrush, Layers } from "lucide-react";
import { API, pushEvent } from "@/lib/api";
import { useContent } from "@/context/ContentContext";

const SERVICES = [
  { key: "Interior", label: "Interior", icon: Home },
  { key: "Exterior", label: "Exterior", icon: Paintbrush },
  { key: "Both", label: "Both", icon: Layers },
];

// Format a US phone as (619) 505-9889 while typing; caps at 10 digits.
function formatPhone(v) {
  const d = (v || "").replace(/\D/g, "").slice(0, 10);
  if (d.length === 0) return "";
  if (d.length < 4) return `(${d}`;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

export default function QuoteForm({ variant = "light", testPrefix = "hero", compact = false }) {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const { content } = useContent();
  const captchaOn = content.captcha_enabled !== false;
  // simple sum that never reaches 10 (a+b <= 9)
  const [captcha] = useState(() => {
    const a = 1 + Math.floor(Math.random() * 8); // 1..8
    const b = 1 + Math.floor(Math.random() * (9 - a)); // 1..(9-a)
    return { a, b };
  });
  const [captchaInput, setCaptchaInput] = useState("");
  const [data, setData] = useState({
    zip_code: "",
    service_type: "Both",
    full_name: "",
    phone: "",
    email: "",
    hp: "",
  });

  const dark = variant === "dark";
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const next = () => {
    setError("");
    if (!data.zip_code.trim()) {
      setError("Please enter your zip code to continue.");
      return;
    }
    setStep(2);
  };

  const submit = async () => {
    setError("");
    if (!data.full_name.trim() || !data.phone.trim() || !data.email.trim()) {
      setError("All fields are required — please fill in your name, phone and email.");
      return;
    }
    if (captchaOn && parseInt(captchaInput, 10) !== captcha.a + captcha.b) {
      setError("Please solve the quick math question so we know you're human.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, data);
      pushEvent("PaintingLead", {
        zip_code: data.zip_code,
        service_type: data.service_type,
        full_name: data.full_name,
        phone: data.phone,
        email: data.email,
        form_location: testPrefix,
      });
      setDone(true);
    } catch (e) {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const labelCls = `block font-semibold uppercase tracking-[0.16em] ${
    compact ? "text-xs mb-1 sm:text-sm sm:mb-2" : "text-sm mb-2"
  } ${dark ? "text-slate-300" : "text-[#1E293B]/70"}`;
  const inputCls = `w-full rounded-lg border-2 border-[#E2E8F0] bg-white px-4 font-medium text-[#0F172A] placeholder:text-slate-400 outline-none transition-colors duration-200 focus:border-[#2563EB] focus:ring-4 focus:ring-[#2563EB]/15 ${
    compact ? "py-2.5 text-base sm:py-3.5 sm:text-lg" : "py-3.5 text-lg"
  }`;
  const mtField = compact ? "mt-2.5 sm:mt-4" : "mt-4";
  const mtBtn = compact ? "mt-3 sm:mt-5" : "mt-5";
  const btnPad = compact ? "py-3 sm:py-4" : "py-4";

  if (done) {
    return (
      <m.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-6"
        data-testid={`${testPrefix}-quote-success`}
      >
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#2563EB]/15">
          <PartyPopper className="h-8 w-8 text-[#2563EB]" />
        </div>
        <h3 className={`font-heading text-2xl font-extrabold mb-2 ${dark ? "text-white" : "text-[#0F172A]"}`}>
          You're all set!
        </h3>
        <p className={`text-base ${dark ? "text-slate-300" : "text-slate-600"}`}>
          Thanks {data.full_name.split(" ")[0]}! A ColorStone estimator will reach out shortly with your
          free, no-obligation quote.
        </p>
      </m.div>
    );
  }

  return (
    <div data-testid={`${testPrefix}-quote-form`}>
      <div className={`flex items-center gap-2 ${compact ? "mb-3 sm:mb-5" : "mb-5"}`}>
        {[1, 2].map((s) => (
          <div key={s} className="flex-1">
            <div
              className={`h-1.5 rounded-full transition-colors duration-300 ${
                step >= s ? "bg-[#2563EB]" : dark ? "bg-white/20" : "bg-[#E2E8F0]"
              }`}
            />
          </div>
        ))}
      </div>
      <p className={`font-semibold uppercase tracking-[0.16em] ${compact ? "text-xs mb-2 sm:text-sm sm:mb-4" : "text-sm mb-4"} ${dark ? "text-slate-300" : "text-[#1E293B]/70"}`}>
        Step {step} of 2
      </p>

      <AnimatePresence mode="wait">
        {step === 1 && (
          <m.div key="s1" initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.2 }}>
            <label className={labelCls}>Your Zip Code <span className="text-[#2563EB]">*</span></label>
            <input
              className={inputCls}
              placeholder="e.g. 90045"
              value={data.zip_code}
              inputMode="numeric"
              maxLength={5}
              required
              onChange={(e) => set("zip_code", e.target.value.replace(/\D/g, "").slice(0, 5))}
              onKeyDown={(e) => e.key === "Enter" && next()}
              data-testid={`${testPrefix}-zip-input`}
            />

            <label className={`${labelCls} ${compact ? "mt-3 sm:mt-5" : "mt-5"}`}>What do you need painted? <span className="text-[#2563EB]">*</span></label>
            <div className="grid grid-cols-3 gap-2">
              {SERVICES.map((s) => {
                const Icon = s.icon;
                const active = data.service_type === s.key;
                return (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => set("service_type", s.key)}
                    data-testid={`${testPrefix}-service-${s.key.toLowerCase()}`}
                    className={`flex flex-col items-center gap-1.5 rounded-lg border-2 py-3 text-sm font-semibold transition-all duration-200 ${
                      active
                        ? "border-[#2563EB] bg-[#2563EB]/10 text-[#2563EB]"
                        : "border-[#E2E8F0] bg-white text-slate-600 hover:border-slate-300"
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    {s.label}
                  </button>
                );
              })}
            </div>

            {error && <p className="mt-3 text-base font-medium text-red-500" data-testid={`${testPrefix}-error`}>{error}</p>}

            <button
              type="button"
              onClick={next}
              data-testid={`${testPrefix}-next-btn`}
              className={`${mtBtn} flex w-full items-center justify-center gap-2 rounded-lg bg-[#2563EB] px-6 ${btnPad} text-lg font-bold text-white transition-transform duration-200 hover:bg-[#1D4ED8] hover:scale-[1.02] active:scale-100`}
            >
              Next <ArrowRight className="h-5 w-5" />
            </button>
          </m.div>
        )}

        {step === 2 && (
          <m.div key="s2" initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.2 }}>
            <label className={labelCls}>Full Name <span className="text-[#2563EB]">*</span></label>
            <input className={inputCls} placeholder="Jane Doe" value={data.full_name} required onChange={(e) => set("full_name", e.target.value)} data-testid={`${testPrefix}-name-input`} />
            <label className={`${labelCls} ${mtField}`}>Phone Number <span className="text-[#2563EB]">*</span></label>
            <input className={inputCls} placeholder="(555) 123-4567" value={data.phone} inputMode="tel" maxLength={14} required onChange={(e) => set("phone", formatPhone(e.target.value))} data-testid={`${testPrefix}-phone-input`} />
            <label className={`${labelCls} ${mtField}`}>Email <span className="text-[#2563EB]">*</span></label>
            <input className={inputCls} placeholder="jane@email.com" value={data.email} inputMode="email" required onChange={(e) => set("email", e.target.value)} data-testid={`${testPrefix}-email-input`} />

            {/* honeypot — hidden from humans, catches bots */}
            <input
              type="text"
              name="company"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              value={data.hp}
              onChange={(e) => set("hp", e.target.value)}
              className="absolute left-[-9999px] h-0 w-0 opacity-0"
            />

            {captchaOn && (
              <>
                <label className={`${labelCls} ${mtField}`}>Human check: what is {captcha.a} + {captcha.b}? <span className="text-[#2563EB]">*</span></label>
                <input
                  className={inputCls}
                  placeholder="Type the answer"
                  value={captchaInput}
                  inputMode="numeric"
                  required
                  onChange={(e) => setCaptchaInput(e.target.value)}
                  data-testid={`${testPrefix}-captcha-input`}
                />
              </>
            )}

            {error && <p className="mt-3 text-base font-medium text-red-500" data-testid={`${testPrefix}-error`}>{error}</p>}

            <button
              type="button"
              onClick={submit}
              disabled={submitting}
              data-testid={`${testPrefix}-submit-btn`}
              className={`${mtBtn} flex w-full items-center justify-center gap-2 rounded-lg bg-[#2563EB] px-6 ${btnPad} text-lg font-bold text-white transition-transform duration-200 hover:bg-[#1D4ED8] hover:scale-[1.02] active:scale-100 disabled:opacity-70`}
            >
              {submitting ? (<><Loader2 className="h-5 w-5 animate-spin" /> Sending...</>) : (<>Get My Free Quote <Check className="h-5 w-5" /></>)}
            </button>
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
}
